import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '@/api/auth';

export function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    aadhaarNumber: '',
    drivingLicense: '',
    vehicleType: '',
    vehicleNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const update = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const markTouched = (key: string) =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const fieldErrors: Record<string, string> = {};
  if (form.name.length > 0 && form.name.length < 3)
    fieldErrors.name = 'Name must be at least 3 characters';
  if (form.phone.length > 0 && !/^[6-9]\d{9}$/.test(form.phone))
    fieldErrors.phone = 'Enter a valid 10-digit phone number';
  if (form.aadhaarNumber.length > 0 && !/^\d{12}$/.test(form.aadhaarNumber))
    fieldErrors.aadhaarNumber = 'Aadhaar must be exactly 12 digits';
  if (form.drivingLicense.length > 0 && form.drivingLicense.length < 5)
    fieldErrors.drivingLicense = 'Enter a valid driving license number';
  if (form.vehicleNumber.length > 0 && form.vehicleNumber.length < 4)
    fieldErrors.vehicleNumber = 'Enter a valid vehicle number';

  const allFilled =
    form.name && form.phone && form.aadhaarNumber &&
    form.drivingLicense && form.vehicleType && form.vehicleNumber;

  const isValid = allFilled && Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.register(form);
      navigate(`/verify-otp/${form.phone}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Become a Rider</h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: 4, fontSize: 15 }}>
          Fill in your details to register
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, display: 'block' }}>Name</label>
          <input
            className="input"
            style={touched.name && fieldErrors.name ? { borderColor: 'var(--danger)' } : {}}
            placeholder="Full name"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            onBlur={() => markTouched('name')}
            required
          />
          {touched.name && fieldErrors.name && (
            <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{fieldErrors.name}</p>
          )}
        </div>

        <div>
          <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, display: 'block' }}>Phone Number</label>
          <input
            className="input"
            style={touched.phone && fieldErrors.phone ? { borderColor: 'var(--danger)' } : {}}
            type="tel"
            inputMode="numeric"
            placeholder="10-digit phone number"
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
            onBlur={() => markTouched('phone')}
            maxLength={10}
            required
          />
          {touched.phone && fieldErrors.phone && (
            <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{fieldErrors.phone}</p>
          )}
        </div>

        <div>
          <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, display: 'block' }}>Aadhaar Number</label>
          <input
            className="input"
            style={touched.aadhaarNumber && fieldErrors.aadhaarNumber ? { borderColor: 'var(--danger)' } : {}}
            type="tel"
            inputMode="numeric"
            placeholder="12-digit Aadhaar number"
            value={form.aadhaarNumber}
            onChange={(e) => update('aadhaarNumber', e.target.value)}
            onBlur={() => markTouched('aadhaarNumber')}
            maxLength={12}
            required
          />
          {touched.aadhaarNumber && fieldErrors.aadhaarNumber && (
            <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{fieldErrors.aadhaarNumber}</p>
          )}
        </div>

        <div>
          <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, display: 'block' }}>Driving License</label>
          <input
            className="input"
            style={touched.drivingLicense && fieldErrors.drivingLicense ? { borderColor: 'var(--danger)' } : {}}
            placeholder="Driving license number"
            value={form.drivingLicense}
            onChange={(e) => update('drivingLicense', e.target.value)}
            onBlur={() => markTouched('drivingLicense')}
            required
          />
          {touched.drivingLicense && fieldErrors.drivingLicense && (
            <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{fieldErrors.drivingLicense}</p>
          )}
        </div>

        <div>
          <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, display: 'block' }}>Vehicle Type</label>
          <select
            className="input"
            value={form.vehicleType}
            onChange={(e) => update('vehicleType', e.target.value)}
            required
          >
            <option value="">Select vehicle type</option>
            <option value="Bike">Bike</option>
            <option value="Scooter">Scooter</option>
            <option value="Bicycle">Bicycle</option>
            <option value="EV">Electric Vehicle</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, display: 'block' }}>Vehicle Number</label>
          <input
            className="input"
            style={touched.vehicleNumber && fieldErrors.vehicleNumber ? { borderColor: 'var(--danger)' } : {}}
            placeholder="e.g. MH01AB1234"
            value={form.vehicleNumber}
            onChange={(e) => update('vehicleNumber', e.target.value.toUpperCase())}
            onBlur={() => markTouched('vehicleNumber')}
            required
          />
          {touched.vehicleNumber && fieldErrors.vehicleNumber && (
            <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{fieldErrors.vehicleNumber}</p>
          )}
        </div>

        {error && <p style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</p>}

        <button className="btn btn-primary btn-full" type="submit" disabled={!isValid || loading}>
          {loading ? 'Registering...' : 'Register & Send OTP'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: 24, color: 'var(--text-secondary)', fontSize: 14 }}>
        Already have an account?{' '}
        <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600 }}>
          Sign in
        </Link>
      </p>
    </div>
  );
}
